// The list views, as data: players, leaders, standings and the thirty teams. P6-329c (GN-2c).
//
// Pure, like `playerModel.js`, and for the same reason: every rule the design states about what these screens say
// is a value a test can assert. Two of them were CORRECTED by the manager against the real data
// (`docs/design-briefs/hoops/RECONCILIATION-seasons.md`) and are the reason this file is not a straight port:
//
// ═══════════════════════════════════════════════════════════════════════════
// "QUALIFIED ON TOTAL" MARKS ONE THING: A PLAYER IN `by_exception[<category>]`
// ═══════════════════════════════════════════════════════════════════════════
//
// The leaders file carries `by_exception: { blk: [1641705], ... }`: the ids of players who are under the games
// minimum and whose TOTAL would still lead a COUNTING stat. 2024-25's blocks board has the one real case, Victor
// Wembanyama, 46 games and 176 blocks. That list, and only that list, is what the label means.
//
// The design had a fallback for a category with no list: mark any leader under `min_gp`. It was drawn against
// samples whose lists were all empty, and on the real data it would put the label on Stephen Curry (43 games) and
// Lauri Markkanen (42) leading free-throw percentage. They are not exceptions. A percentage qualifies on MAKES, not
// games (`min_makes`), so a leader under 58 games is the NORMAL rule and carries no label. The fallback is not here.
// `by_exception` has no `fg_pct` or `ft_pct` key and never will; a category with no list marks nobody.
//
// ═══════════════════════════════════════════════════════════════════════════
// A NULL IN A LAST-TEN LINE IS A GAME HE DID NOT ATTEMPT ONE, AND IT IS PRINTED AS A DASH
// ═══════════════════════════════════════════════════════════════════════════
//
// A percentage leader's `last10` has `null` where he took no shot of that kind. The line skips it (`chartMath.js`),
// and the ten values printed under the hero's line print a dash there, so the row of numbers still has ten of them
// and the dash is a visible fact rather than a gap that shifts the rest.

import {
  CAT_NAME,
  LEAD_CATS,
  STAT,
  fmt,
  fmtDelta,
  fold,
  isNumber,
  isPct,
  ord,
  parseRec,
  rec,
  wl,
} from './historyFormat.js'
import { miniTrajectory, sparkline, trajectoryDomain } from './chartMath.js'

/** The index sorted by name, narrowed by what was typed (accents and case folded away), as the rows the list draws. */
export function playersList(index, query) {
  const players = (Array.isArray(index?.players) ? index.players : []).filter(
    (entry) => entry && typeof entry.name === 'string' && Number.isInteger(entry.id),
  )
  const typed = String(query ?? '').trim()
  const wanted = fold(typed)
  const all = [...players].sort((a, b) => a.name.localeCompare(b.name))
  const hits = wanted === '' ? all : all.filter((entry) => fold(entry.name).includes(wanted))
  return {
    total: all.length,
    typed,
    none: hits.length === 0,
    rows: hits.map((entry) => ({
      id: entry.id,
      name: entry.name,
      team: entry.team,
      teamId: entry.team_id,
      sub: [entry.team, entry.pos, entry.gp ? `${entry.gp} GP` : null].filter(Boolean).join(' · '),
    })),
  }
}

/** The ids of the players the leaders file lists as qualifying on total for one category. Integers only, a closed read. */
export function exceptionIds(leaders, cat) {
  const list = Array.isArray(leaders?.by_exception?.[cat]) ? leaders.by_exception[cat] : []
  return new Set(list.filter((id) => Number.isInteger(id)))
}

/** A last-ten value as printed under the hero's line: a percentage as a whole number, a count as it is, a dash for none. */
function printedLast(cat, value) {
  if (!isNumber(value)) return '–'
  return isPct(cat) ? String(Math.round(value * 100)) : String(value)
}

/**
 * One leaders category: the heading, the rule, the #1 hero, and ranks 2 to 10.
 *
 * `nameOf(teamId, abbr)` is the team's full name where the board's table has it. `cat` that is not a category is
 * points. `hasSparks` is whether ANY row carries a last-ten line, which is what decides if the note talks about one.
 */
export function leadersModel({ leaders, cat, nameOf }) {
  const chosen = LEAD_CATS.includes(cat) ? cat : 'pts'
  const rows = Array.isArray(leaders?.categories?.[chosen]) ? leaders.categories[chosen] : []
  const exceptions = exceptionIds(leaders, chosen)
  const pct = isPct(chosen)
  const minMakes = leaders?.min_makes?.[chosen]
  const pool = leaders?.pools?.[chosen]
  const poolText = pool == null ? '' : ` · pool ${pool}`
  const hasLast = (row) => Array.isArray(row?.last10) && row.last10.length > 1
  const hasSparks = rows.some(hasLast)
  const sparkSentence = hasSparks ? ' The line is his last ten games; the dotted line is his season mark.' : ''

  const toRow = (row) => ({
    rank: row.rank,
    playerId: row.player_id,
    name: row.name,
    team: row.team,
    teamId: row.team_id,
    games: row.gp,
    sub: `${row.team} · ${row.gp} GP`,
    value: fmt(chosen, row.value),
    exception: exceptions.has(row.player_id),
    spark: hasLast(row) ? sparkline(row.last10, row.value, 30) : null,
  })

  const top = rows[0]
  const next = rows[1]
  let hero = null
  if (top) {
    const delta = next ? fmtDelta(chosen, top.value - next.value) : null
    hero = {
      ...toRow(top),
      sub: `${nameOf(top.team_id, top.team)} · ${top.gp} GP`,
      unit: pct ? STAT[chosen] : `${STAT[chosen]} per game`,
      gap: next === undefined ? '' : delta === 'level' ? `Level with ${next.name}` : `${delta} ahead of ${next.name}`,
      spark: hasLast(top) ? sparkline(top.last10, top.value, 48) : null,
      last10: hasLast(top) ? top.last10.map((value) => printedLast(chosen, value)) : [],
    }
  }

  return {
    cat: chosen,
    title: CAT_NAME[chosen],
    unit: pct ? STAT[chosen] : 'Per gm',
    rule:
      pct && minMakes
        ? `Min ${minMakes} makes${poolText}`
        : `Min ${leaders?.min_gp} of ${leaders?.games} games${poolText}`,
    note: pct
      ? `Percentages qualify on makes, not games: a player with fewer than ${leaders?.min_gp} games can still lead if he reached ${minMakes} makes.${sparkSentence}`
      : `Ranked per game among players with ${leaders?.min_gp} or more games, plus anyone who qualifies on total.${sparkSentence}`,
    hasSparks,
    hero,
    rows: rows.slice(1).map(toRow),
  }
}

/** Games over .500 as a record of the home and road splits: what is left over is neutral-site games, or `null` for none. */
function neutralRecord(row) {
  const [homeWins, homeLosses] = parseRec(row.home)
  const [roadWins, roadLosses] = parseRec(row.road)
  const wins = row.w - homeWins - roadWins
  const losses = row.l - homeLosses - roadLosses
  return wins + losses > 0 ? wl(wins, losses) : null
}

/** A clinch code with the NBA's leading dash and spaces taken off: `- pi` is `pi`. */
export const clinchCode = (value) => String(value ?? '').replace(/^[-\s]+/, '').trim()

/**
 * The standings: each conference's rows in order, with the dashed rule above the first play-in team and the first
 * eliminated one, and a small line of games over .500 on a scale every row shares.
 *
 * `nameOf(teamId, abbr)` is the full name where the board's table has it, and the row's own name otherwise.
 */
export function standingsModel({ standings, nameOf }) {
  const conferences = standings?.conferences ?? {}
  const domain = trajectoryDomain(standings)
  return {
    domain,
    conferences: Object.entries(conferences).map(([name, rows]) => {
      let sawPlayIn = false
      let sawOut = false
      return {
        name,
        rows: (Array.isArray(rows) ? rows : []).map((row) => {
          const code = clinchCode(row.clinch)
          let cut = null
          if (code === 'pi' && !sawPlayIn) {
            sawPlayIn = true
            cut = 'Play-in'
          }
          if (code === 'o' && !sawOut) {
            sawOut = true
            cut = 'Eliminated'
          }
          const neutral = neutralRecord(row)
          const trajectory = standings?.trajectories?.[row.team_id] ?? null
          const parts = [`Home ${rec(row.home)}`, `Road ${rec(row.road)}`, neutral ? `Neutral ${neutral}` : null].filter(Boolean)
          return {
            rank: row.rank,
            abbr: row.abbr,
            teamId: row.team_id,
            name: nameOf(row.team_id, row.abbr, row.name),
            w: row.w,
            l: row.l,
            pct: Number(row.pct).toFixed(3).replace(/^0/, ''),
            gb: row.gb === 0 ? '—' : String(row.gb),
            // The records under a team, as one line and as its parts: the line wraps BETWEEN records and never inside one,
            // so `Neutral 1–0` is not split across two lines at its dash, which the parts are for.
            sub: parts.join(' · '),
            parts,
            cut,
            trajectory,
            mini: trajectory ? miniTrajectory(trajectory, domain, 20) : null,
          }
        }),
      }
    }),
  }
}

/** The thirty teams as tiles, grouped by conference and sorted by name, each with its record, rank and season line. */
export function teamsModel({ standings, namesOf }) {
  const domain = trajectoryDomain(standings)
  const groups = Object.entries(standings?.conferences ?? {}).map(([name, rows]) => ({
    name,
    rows: [...(Array.isArray(rows) ? rows : [])]
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      .map((row) => {
        const trajectory = standings?.trajectories?.[row.team_id] ?? null
        const names = namesOf(row.team_id, row.abbr, row.name)
        return {
          abbr: row.abbr,
          teamId: row.team_id,
          city: names.city,
          nick: names.nick,
          record: wl(row.w, row.l),
          rank: `${ord(row.rank)} ${name}`,
          mini: trajectory ? miniTrajectory(trajectory, domain, 32) : null,
        }
      }),
  }))
  return { groups, count: groups.reduce((sum, group) => sum + group.rows.length, 0) }
}
